import React from 'react';
import { useShow, IResourceComponentsProps } from '@refinedev/core';
import { Show, DateField } from '@refinedev/antd';
import { Typography, Tag, Descriptions, Card, Row, Col, Statistic } from 'antd';
import { describeIosPrice } from './iosPrice';

const { Title } = Typography;

export const ProductShow: React.FC<IResourceComponentsProps> = () => {
  const { queryResult } = useShow();
  const { data, isLoading } = queryResult;
  const record = data?.data;

  const items = record?.items || {};
  const price = record?.price || {};
  const inGamePrice = record?.inGamePrice || {};

  return (
    <Show isLoading={isLoading}>
      <Title level={5}>Product Details</Title>
      <Descriptions bordered column={2} size="small">
        <Descriptions.Item label="ID">
          <code style={{ fontSize: 11 }}>{record?.id}</code>
        </Descriptions.Item>
        <Descriptions.Item label="Store Product ID">
          <code>{record?.storeProductId}</code>
        </Descriptions.Item>
        <Descriptions.Item label="Name">{record?.name}</Descriptions.Item>
        <Descriptions.Item label="Description">{record?.description || '-'}</Descriptions.Item>
        <Descriptions.Item label="Type">
          <Tag>{record?.type}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Category">
          <Tag color="blue">{record?.category}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Status">
          <Tag color={record?.isActive ? 'green' : 'red'}>
            {record?.isActive ? 'Active' : 'Inactive'}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Flags">
          {record?.isFeatured && <Tag color="blue">Featured</Tag>}
          {record?.bestValue && <Tag color="gold">Best Value</Tag>}
          {!record?.isFeatured && !record?.bestValue && '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Sort Order">{record?.sortOrder}</Descriptions.Item>
        <Descriptions.Item label="Platform">{record?.platform}</Descriptions.Item>
        <Descriptions.Item label="Created At">
          <DateField value={record?.createdAt} />
        </Descriptions.Item>
        <Descriptions.Item label="Updated At">
          <DateField value={record?.updatedAt} />
        </Descriptions.Item>
      </Descriptions>

      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col xs={24} sm={12}>
          <Card title="Items (Player Receives)" size="small">
            <Row gutter={[16, 8]}>
              {items.coins ? (
                <Col span={12}>
                  <Statistic title="Coins" value={items.coins} />
                </Col>
              ) : null}
              {items.matchTokens ? (
                <Col span={12}>
                  <Statistic title="Match Tokens" value={items.matchTokens} />
                </Col>
              ) : null}
              {items.evoTokens ? (
                <Col span={12}>
                  <Statistic title="Evo Tokens" value={items.evoTokens} />
                </Col>
              ) : null}
              {items.gems ? (
                <Col span={12}>
                  <Statistic title="Gems" value={items.gems} />
                </Col>
              ) : null}
              {items.cards ? (
                <Col span={12}>
                  <Statistic
                    title="Cards"
                    value={items.cards}
                    suffix={
                      items.cardRarity !== undefined
                        ? `(Rarity: ${['Rookie', 'Semi-Pro', 'Pro', 'Legendary'][items.cardRarity] || items.cardRarity})`
                        : ''
                    }
                  />
                </Col>
              ) : null}
              {!items.coins &&
                !items.matchTokens &&
                !items.evoTokens &&
                !items.gems &&
                !items.cards && (
                  <Col span={24}>
                    <span style={{ color: '#999' }}>No items configured</span>
                  </Col>
                )}
            </Row>
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card title="Pricing" size="small">
            <Descriptions column={1} size="small">
              <Descriptions.Item label="USD">
                {price.usd !== undefined ? `$${price.usd}` : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="INR">
                {price.inr !== undefined ? `₹${price.inr}` : '-'}
              </Descriptions.Item>
            </Descriptions>
            <Title level={5} style={{ marginTop: 8, fontSize: 13 }}>
              iOS Price
            </Title>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="App Store">
                {record?.iosPrice ? (
                  describeIosPrice(record.iosPrice)
                ) : (
                  <span style={{ color: '#999' }}>{describeIosPrice(null)}</span>
                )}
              </Descriptions.Item>
            </Descriptions>
            <Title level={5} style={{ marginTop: 8, fontSize: 13 }}>
              In-Game Price
            </Title>
            <Descriptions column={1} size="small">
              {inGamePrice.coins && (
                <Descriptions.Item label="Coins">{inGamePrice.coins}</Descriptions.Item>
              )}
              {inGamePrice.matchTokens && (
                <Descriptions.Item label="Match Tokens">
                  {inGamePrice.matchTokens}
                </Descriptions.Item>
              )}
              {inGamePrice.evoTokens && (
                <Descriptions.Item label="Evo Tokens">{inGamePrice.evoTokens}</Descriptions.Item>
              )}
              {inGamePrice.premiumCurrency && (
                <Descriptions.Item label="Premium">{inGamePrice.premiumCurrency}</Descriptions.Item>
              )}
              {!inGamePrice.coins &&
                !inGamePrice.matchTokens &&
                !inGamePrice.evoTokens &&
                !inGamePrice.premiumCurrency && (
                  <Descriptions.Item label="">
                    <span style={{ color: '#999' }}>None</span>
                  </Descriptions.Item>
                )}
            </Descriptions>
          </Card>
        </Col>
      </Row>
    </Show>
  );
};
